import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import toast from 'react-hot-toast';
import { generateNFTImage, downloadNFTImage } from '../utils/nftImageGenerator';

/**
 * V3 Admin Panel - Full Version
 */
const AdminV3 = () => {
  const { t } = useTranslation();
  const {
    account,
    isAdmin,
    requestDraw,
    getAdminInfo,
    nftStats,
    connectWallet,
    isConnecting,
    getOverviewStats,
    getAllSalesStats,
    getAllDividendStats,
    getNFTHolderRankings
  } = useWeb3V3();

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [overview, setOverview] = useState(null);
  const [salesStats, setSalesStats] = useState([]);
  const [dividendStats, setDividendStats] = useState([]);
  const [holderRankings, setHolderRankings] = useState([]);
  const [countdown, setCountdown] = useState('');

  // Notification states
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyType, setNotifyType] = useState('telegram');
  const [sending, setSending] = useState(false);
  const [notifyServiceStatus, setNotifyServiceStatus] = useState(null);

  // NFT Image states
  const [previewNumber, setPreviewNumber] = useState('123456');
  const [previewSignature, setPreviewSignature] = useState('Lucky Ticket');
  const [previewPrizeLevel, setPreviewPrizeLevel] = useState(-1);
  const [previewImage, setPreviewImage] = useState(null);

  const loadData = useCallback(async () => {
    if (!account || !isAdmin()) return;
    try {
      setLoading(true);
      const [info, overviewData, sales, dividends, rankings] = await Promise.all([
        getAdminInfo(),
        getOverviewStats(),
        getAllSalesStats(),
        getAllDividendStats(),
        getNFTHolderRankings(20)
      ]);
      setAdminInfo(info);
      setOverview(overviewData);
      setSalesStats(sales);
      setDividendStats(dividends);
      setHolderRankings(rankings);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [account, isAdmin, getAdminInfo, getOverviewStats, getAllSalesStats, getAllDividendStats, getNFTHolderRankings]);

  useEffect(() => {
    if (account && isAdmin()) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [account, loadData, isAdmin]);

  useEffect(() => {
    if (!adminInfo) return;
    const updateCountdown = () => {
      if (!adminInfo.lastDrawTime || adminInfo.lastDrawTime === '0') {
        setCountdown(t('admin.draw.canDrawNow'));
        return;
      }
      const nextDrawTime = parseInt(adminInfo.lastDrawTime) + parseInt(adminInfo.drawInterval);
      const now = Math.floor(Date.now() / 1000);
      const diff = nextDrawTime - now;
      if (diff <= 0) {
        setCountdown(t('admin.draw.canDraw'));
        return;
      }
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      setCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [adminInfo, t]);

  const handleRequestDraw = async () => {
    try {
      setDrawing(true);
      await requestDraw();
      await loadData();
    } catch (error) {
      toast.error(error.message || t('admin.draw.failed'));
    } finally {
      setDrawing(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp || timestamp === '0') return '-';
    return new Date(parseInt(timestamp) * 1000).toLocaleString();
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Check notification service status
  const checkNotifyService = useCallback(async () => {
    try {
      const response = await fetch('http://localhost:3001/health');
      const data = await response.json();
      setNotifyServiceStatus(data);
    } catch (e) {
      setNotifyServiceStatus(null);
    }
  }, []);

  useEffect(() => {
    checkNotifyService();
    const interval = setInterval(checkNotifyService, 30000);
    return () => clearInterval(interval);
  }, [checkNotifyService]);

  // Generate preview image
  const handleGeneratePreview = useCallback(() => {
    const image = generateNFTImage({
      number: previewNumber,
      signature: previewSignature,
      drawId: 1,
      tokenId: 1,
      totalDividends: '25.00',
      totalPrizes: '100.00',
      prizeLevel: parseInt(previewPrizeLevel),
      isListed: false
    });
    setPreviewImage(image);
  }, [previewNumber, previewSignature, previewPrizeLevel]);

  // Generate initial preview
  useEffect(() => {
    handleGeneratePreview();
  }, [handleGeneratePreview]);

  // If wallet not connected, return connect page
  if (!account) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">{t('admin.title')}</h1>
          <p className="text-gray-400 mb-8">{t('admin.pleaseConnectAdmin')}</p>
          <button onClick={connectWallet} disabled={isConnecting}
            className="px-8 py-3 bg-white text-black font-bold hover:bg-gray-200 transition-colors">
            {isConnecting ? t('common.connecting') : t('common.connectWallet')}
          </button>
        </div>
      </div>
    );
  }

  // If not admin, return access denied page
  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">{t('admin.accessDenied')}</h1>
          <p className="text-gray-400 mb-4">{t('admin.onlyAdmin')}</p>
          <p className="text-sm text-gray-600">{t('admin.currentAccount')}: {formatAddress(account)}</p>
        </div>
      </div>
    );
  }

  // Send notification
  const handleSendNotification = async () => {
    if (!notifyMessage.trim()) {
      toast.error(t('admin.notify.emptyMessage'));
      return;
    }
    try {
      setSending(true);
      const response = await fetch('http://localhost:3001/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: notifyType, message: notifyMessage })
      });
      if (response.ok) {
        toast.success(t('admin.notify.sent'));
        setNotifyMessage('');
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      toast.error(t('admin.notify.failed'));
    } finally {
      setSending(false);
    }
  };

  // Test draw notification
  const handleTestDrawNotify = async () => {
    try {
      await fetch('http://localhost:3001/test/draw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drawId: overview?.currentDrawId || 1, winningNumber: Math.floor(Math.random() * 100000) })
      });
      toast.success(t('admin.notify.testSent'));
    } catch (e) {
      toast.error(t('admin.notify.failed'));
    }
  };

  // Test dividend notification
  const handleTestDividendNotify = async () => {
    try {
      await fetch('http://localhost:3001/test/dividend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drawId: overview?.currentDrawId || 1, totalPool: '1000', nftCount: 100, perNFT: '10' })
      });
      toast.success(t('admin.notify.testSent'));
    } catch (e) {
      toast.error(t('admin.notify.failed'));
    }
  };

  // Download preview image
  const handleDownloadPreview = () => {
    downloadNFTImage({
      number: previewNumber,
      signature: previewSignature,
      drawId: 1,
      tokenId: 1,
      totalDividends: '25.00',
      totalPrizes: '100.00',
      prizeLevel: parseInt(previewPrizeLevel),
      isListed: false
    }, `NFT-${previewNumber}-preview.png`);
  };

  const tabs = [
    { id: 'overview', label: t('admin.tabs.overview') },
    { id: 'draw', label: t('admin.tabs.draw') },
    { id: 'sales', label: t('admin.tabs.sales') },
    { id: 'dividends', label: t('admin.tabs.dividends') },
    { id: 'holders', label: t('admin.tabs.holders') },
    { id: 'notify', label: t('admin.tabs.notify') },
    { id: 'images', label: t('admin.tabs.images') }
  ];

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold">{t('admin.title')}</h1>
        </div>

        <div className="flex border-b border-gray-800 mb-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'text-white border-b-2 border-white' : 'text-gray-500 hover:text-white'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="mt-4 text-gray-400">{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="border border-gray-800 p-6">
                    <p className="text-gray-500 text-sm mb-1">{t('admin.overview.totalMinted')}</p>
                    <p className="text-3xl font-bold">{overview?.totalMinted || nftStats?.totalMinted || '0'}</p>
                  </div>
                  <div className="border border-gray-800 p-6">
                    <p className="text-gray-500 text-sm mb-1">{t('admin.overview.remaining')}</p>
                    <p className="text-3xl font-bold text-green-400">{overview?.remaining || '100000'}</p>
                  </div>
                  <div className="border border-gray-800 p-6">
                    <p className="text-gray-500 text-sm mb-1">{t('admin.overview.prizePool')}</p>
                    <p className="text-3xl font-bold text-yellow-400">${nftStats?.prizePoolBalance || '0'}</p>
                  </div>
                  <div className="border border-gray-800 p-6">
                    <p className="text-gray-500 text-sm mb-1">{t('admin.overview.currentDraw')}</p>
                    <p className="text-3xl font-bold">{t('admin.overview.period')} {overview?.currentDrawId || '1'}</p>
                  </div>
                  <div className="border border-gray-800 p-6">
                    <p className="text-gray-500 text-sm mb-1">{t('admin.overview.activeOrders')}</p>
                    <p className="text-3xl font-bold">{overview?.activeOrderCount || '0'}</p>
                  </div>
                </div>
                {overview?.currentSales && (
                  <div className="border border-gray-800 p-6">
                    <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.overview.currentPeriodSales')}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div><p className="text-gray-500 text-sm mb-1">{t('admin.sales.totalSales')}</p><p className="text-xl font-bold">${overview.currentSales.totalSales}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{t('admin.sales.usdtSales')}</p><p className="text-xl font-bold">${overview.currentSales.usdtSales}</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{t('admin.sales.ethSales')}</p><p className="text-xl font-bold">{overview.currentSales.ethSales} ETH</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{t('admin.sales.nftCount')}</p><p className="text-xl font-bold">{overview.currentSales.nftCount}</p></div>
                    </div>
                  </div>
                )}
                <div className="border border-gray-800 p-6">
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.overview.accountInfo')}</h2>
                  <div className="space-y-4">
                    <div><p className="text-gray-500 text-sm mb-1">{t('admin.overview.adminAddress')}</p><p className="font-mono text-sm break-all">{account}</p></div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 bg-green-500 rounded-full"></span><span className="text-green-400">{t('admin.overview.verified')}</span></div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'draw' && (
              <div className="border border-gray-800 p-6">
                <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.draw.management')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                  <div><p className="text-gray-500 text-sm mb-1">{t('admin.draw.currentPeriod')}</p><p className="text-2xl font-bold">{adminInfo?.currentDrawId || '1'}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{t('admin.draw.lastDraw')}</p><p className="text-lg">{formatTime(adminInfo?.lastDrawTime)}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{t('admin.draw.interval')}</p><p className="text-lg">{adminInfo?.drawInterval ? `${Math.floor(parseInt(adminInfo.drawInterval) / 86400)} days` : '-'}</p></div>
                </div>
                <div className="mb-6"><p className="text-gray-500 text-sm mb-2">{t('admin.draw.nextDrawCountdown')}</p><p className="text-3xl font-mono font-bold text-green-400">{countdown}</p></div>
                <button onClick={handleRequestDraw} disabled={drawing}
                  className={`w-full py-4 text-lg font-bold transition-colors ${drawing ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-white text-black hover:bg-gray-200'}`}>
                  {drawing ? <span className="flex items-center justify-center gap-2"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>{t('admin.draw.drawing')}</span> : t('admin.draw.drawNow')}
                </button>
                <p className="mt-4 text-sm text-gray-500 text-center">{t('admin.draw.description')}</p>
              </div>
            )}

            {activeTab === 'sales' && (
              <div className="border border-gray-800 p-6">
                <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.sales.history')}</h2>
                {salesStats.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="text-left text-gray-500 text-sm">
                        <th className="py-3 px-4">{t('admin.sales.period')}</th><th className="py-3 px-4">{t('admin.sales.totalSales')}</th>
                        <th className="py-3 px-4">{t('admin.sales.usdtSales')}</th><th className="py-3 px-4">{t('admin.sales.ethSales')}</th>
                        <th className="py-3 px-4">{t('admin.sales.nftCount')}</th><th className="py-3 px-4">{t('admin.sales.dividendPool')}</th>
                        <th className="py-3 px-4">{t('admin.sales.prizePool')}</th>
                      </tr></thead>
                      <tbody>{salesStats.map((stat) => (
                        <tr key={stat.drawId} className="border-t border-gray-800">
                          <td className="py-3 px-4 font-bold">{t('admin.overview.period')} {stat.drawId}</td>
                          <td className="py-3 px-4">${stat.totalSales}</td><td className="py-3 px-4">${stat.usdtSales}</td>
                          <td className="py-3 px-4">{stat.ethSales} ETH</td><td className="py-3 px-4">{stat.nftCount}</td>
                          <td className="py-3 px-4 text-green-400">${stat.dividendPool}</td><td className="py-3 px-4 text-yellow-400">${stat.prizePool}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ) : <div className="text-center py-12 text-gray-500">{t('admin.sales.empty')}</div>}
              </div>
            )}

            {activeTab === 'dividends' && (
              <div className="border border-gray-800 p-6">
                <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.dividends.history')}</h2>
                {dividendStats.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="text-left text-gray-500 text-sm">
                        <th className="py-3 px-4">{t('admin.dividends.period')}</th><th className="py-3 px-4">{t('admin.dividends.totalPool')}</th>
                        <th className="py-3 px-4">{t('admin.dividends.nftCount')}</th><th className="py-3 px-4">{t('admin.dividends.perNFT')}</th>
                        <th className="py-3 px-4">{t('admin.dividends.claimed')}</th><th className="py-3 px-4">{t('admin.dividends.time')}</th>
                        <th className="py-3 px-4">{t('admin.dividends.status')}</th>
                      </tr></thead>
                      <tbody>{dividendStats.map((stat) => (
                        <tr key={stat.drawId} className="border-t border-gray-800">
                          <td className="py-3 px-4 font-bold">{t('admin.overview.period')} {stat.drawId}</td>
                          <td className="py-3 px-4">${stat.totalDividendPool}</td><td className="py-3 px-4">{stat.soldNFTCount}</td>
                          <td className="py-3 px-4 text-green-400">${stat.perNFTDividend}</td><td className="py-3 px-4">${stat.totalClaimed}</td>
                          <td className="py-3 px-4 text-sm">{formatTime(stat.timestamp)}</td>
                          <td className="py-3 px-4">{stat.finalized ? <span className="px-2 py-1 bg-green-900 text-green-400 text-xs rounded">{t('admin.dividends.finalized')}</span> : <span className="px-2 py-1 bg-yellow-900 text-yellow-400 text-xs rounded">{t('admin.dividends.pending')}</span>}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ) : <div className="text-center py-12 text-gray-500">{t('admin.dividends.empty')}</div>}
              </div>
            )}

            {activeTab === 'holders' && (
              <div className="border border-gray-800 p-6">
                <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.holders.title')} (Top 20)</h2>
                {holderRankings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="text-left text-gray-500 text-sm">
                        <th className="py-3 px-4">{t('admin.holders.rank')}</th><th className="py-3 px-4">{t('admin.holders.address')}</th>
                        <th className="py-3 px-4">{t('admin.holders.count')}</th><th className="py-3 px-4">{t('admin.holders.percentage')}</th>
                      </tr></thead>
                      <tbody>{holderRankings.map((holder, index) => {
                        const totalMinted = parseInt(overview?.totalMinted || nftStats?.totalMinted || '0');
                        const percentage = totalMinted > 0 ? ((holder.count / totalMinted) * 100).toFixed(2) : '0.00';
                        return (
                          <tr key={holder.address} className="border-t border-gray-800">
                            <td className="py-3 px-4">{index < 3 ? <span className={`text-xl ${index === 0 ? 'text-yellow-400' : index === 1 ? 'text-gray-300' : 'text-amber-600'}`}>{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span> : <span className="text-gray-500">#{index + 1}</span>}</td>
                            <td className="py-3 px-4 font-mono text-sm">{formatAddress(holder.address)}{holder.address.toLowerCase() === account.toLowerCase() && <span className="ml-2 text-green-400 text-xs">({t('admin.holders.you')})</span>}</td>
                            <td className="py-3 px-4 font-bold text-lg">{holder.count}</td>
                            <td className="py-3 px-4"><div className="flex items-center gap-2"><div className="flex-1 bg-gray-800 rounded-full h-2 max-w-24"><div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(parseFloat(percentage), 100)}%` }} /></div><span className="text-sm text-gray-400">{percentage}%</span></div></td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                ) : <div className="text-center py-12 text-gray-500">{t('admin.holders.empty')}</div>}
              </div>
            )}

            {activeTab === 'notify' && (
              <div className="space-y-8">
                {/* Service Status */}
                <div className="border border-gray-800 p-6">
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.notify.serviceStatus')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${notifyServiceStatus ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <div>
                        <p className="text-gray-500 text-sm">{t('admin.notify.service')}</p>
                        <p className="font-medium">{notifyServiceStatus ? t('admin.notify.online') : t('admin.notify.offline')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${notifyServiceStatus?.services?.telegram ? 'bg-green-500' : 'bg-gray-600'}`}></span>
                      <div>
                        <p className="text-gray-500 text-sm">Telegram</p>
                        <p className="font-medium">{notifyServiceStatus?.services?.telegram ? t('admin.notify.connected') : t('admin.notify.notConfigured')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${notifyServiceStatus?.services?.email ? 'bg-green-500' : 'bg-gray-600'}`}></span>
                      <div>
                        <p className="text-gray-500 text-sm">Email</p>
                        <p className="font-medium">{notifyServiceStatus?.services?.email ? t('admin.notify.connected') : t('admin.notify.notConfigured')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${notifyServiceStatus?.services?.blockchain ? 'bg-green-500' : 'bg-gray-600'}`}></span>
                      <div>
                        <p className="text-gray-500 text-sm">Blockchain</p>
                        <p className="font-medium">{notifyServiceStatus?.services?.blockchain ? t('admin.notify.listening') : t('admin.notify.notConnected')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Send Notification */}
                <div className="border border-gray-800 p-6">
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.notify.sendNotification')}</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-500 text-sm mb-2">{t('admin.notify.channel')}</label>
                      <select value={notifyType} onChange={(e) => setNotifyType(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 px-4 py-2 focus:outline-none focus:border-white">
                        <option value="telegram">Telegram</option>
                        <option value="all">{t('admin.notify.all')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-500 text-sm mb-2">{t('admin.notify.message')}</label>
                      <textarea value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)}
                        placeholder={t('admin.notify.messagePlaceholder')} rows={4}
                        className="w-full bg-gray-900 border border-gray-700 px-4 py-2 focus:outline-none focus:border-white resize-none" />
                    </div>
                    <button onClick={handleSendNotification} disabled={sending || !notifyServiceStatus}
                      className={`w-full py-3 font-bold transition-colors ${sending || !notifyServiceStatus ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-white text-black hover:bg-gray-200'}`}>
                      {sending ? t('admin.notify.sending') : t('admin.notify.send')}
                    </button>
                  </div>
                </div>

                {/* Test Notifications */}
                <div className="border border-gray-800 p-6">
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.notify.testNotifications')}</h2>
                  <p className="text-gray-500 text-sm mb-4">{t('admin.notify.testDescription')}</p>
                  <div className="flex gap-4">
                    <button onClick={handleTestDrawNotify} disabled={!notifyServiceStatus}
                      className="flex-1 py-3 border border-gray-700 text-gray-300 hover:text-white hover:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {t('admin.notify.testDraw')}
                    </button>
                    <button onClick={handleTestDividendNotify} disabled={!notifyServiceStatus}
                      className="flex-1 py-3 border border-gray-700 text-gray-300 hover:text-white hover:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {t('admin.notify.testDividend')}
                    </button>
                  </div>
                </div>

                {/* Setup Guide */}
                {!notifyServiceStatus && (
                  <div className="border border-yellow-800 bg-yellow-900/20 p-6">
                    <h2 className="text-xl font-bold mb-4 text-yellow-400">{t('admin.notify.setupGuide')}</h2>
                    <div className="text-gray-300 space-y-2">
                      <p>1. {t('admin.notify.step1')}</p>
                      <p>2. {t('admin.notify.step2')}</p>
                      <p>3. {t('admin.notify.step3')}</p>
                      <p>4. {t('admin.notify.step4')}</p>
                    </div>
                    <code className="block mt-4 p-3 bg-black text-green-400 text-sm">
                      cd notification-service && npm install && npm start
                    </code>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'images' && (
              <div className="space-y-8">
                {/* NFT Image Preview */}
                <div className="border border-gray-800 p-6">
                  <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-800">{t('admin.images.preview')}</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Preview Image */}
                    <div className="flex justify-center">
                      {previewImage && (
                        <img src={previewImage} alt="NFT Preview" className="max-w-[300px] border border-gray-700" />
                      )}
                    </div>

                    {/* Settings */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-500 text-sm mb-2">{t('admin.images.number')}</label>
                        <input
                          type="text"
                          value={previewNumber}
                          onChange={(e) => setPreviewNumber(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="w-full bg-gray-900 border border-gray-700 px-4 py-2 font-mono text-xl focus:outline-none focus:border-white"
                          placeholder="000000"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-500 text-sm mb-2">{t('admin.images.signature')}</label>
                        <input
                          type="text"
                          value={previewSignature}
                          onChange={(e) => setPreviewSignature(e.target.value)}
                          maxLength={50}
                          className="w-full bg-gray-900 border border-gray-700 px-4 py-2 focus:outline-none focus:border-white"
                          placeholder="Personal signature..."
                        />
                      </div>

                      <div>
                        <label className="block text-gray-500 text-sm mb-2">{t('admin.images.prizeLevel')}</label>
                        <select
                          value={previewPrizeLevel}
                          onChange={(e) => setPreviewPrizeLevel(e.target.value)}
                          className="w-full bg-gray-900 border border-gray-700 px-4 py-2 focus:outline-none focus:border-white"
                        >
                          <option value="-1">{t('admin.images.noPrize')}</option>
                          <option value="0">{t('admin.images.grandPrize')}</option>
                          <option value="1">{t('admin.images.firstPrize')}</option>
                          <option value="2">{t('admin.images.secondPrize')}</option>
                          <option value="3">{t('admin.images.thirdPrize')}</option>
                        </select>
                      </div>

                      <button
                        onClick={handleDownloadPreview}
                        className="w-full py-3 bg-white text-black font-bold hover:bg-gray-200 transition-colors"
                      >
                        {t('admin.images.download')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-8 text-center">
          <button onClick={loadData} disabled={loading} className="px-6 py-2 border border-gray-700 text-gray-400 hover:text-white hover:border-white transition-colors">
            {loading ? t('common.loading') : t('admin.refresh')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminV3;
